import {
  DateRange,
  TimestampRange,
  createGetter,
  guid,
  isNullOrUndefined,
  isNullOrUndefinedOrEmpty,
  utc
}                                     from 'rewire-common';
import {
  createWatcherFn,
  // freeze,
  observable,
  WatcherTypeFn,
}                                     from 'rewire-core';
import {
  createColumn,
  createGrid,
  ICell,
  IColumn,
  IGrid,
  IGridOptions,
  IRowData
}                                     from 'rewire-grid';
import {
  AuditLogEntryType,
  AuditLogEntryTypes,
  IAuditLog,
  _auditLogEntryTypeMap,
  _auditLogEntryTypeToUdfTypeMap,
  numberColumn,
}                                     from '../DataTypes';
import DataQueries                    from '../graphql/Queries';
import {
  arrayMapper,
  codeMapper,
  dateMapper,
  getDifferenceMapper,
  getDifferenceCellTooltip,
  nameMapper,
  numberStringMapper,
  dateTimeMapper,
}                                     from '../utility/DataConverters';
import {
  formatTime,
}                                     from '../utility/DateUtils';
import AuditLogDifferenceCellRenderer, {
  AuditLogDifferenceCellStringArrayRenderer,
}                                     from './AuditLogDifferenceCellRenderer';
import {
  formatUdfValue,
  getPhoneString
}                                     from '../utility/Formatters';
import auditLogListModel              from './AuditLogListModel';
import udfsSubscriptionService        from '../services/UdfsSubscriptionService';
import authService                    from '../services/AuthService';

export const scheduleDisabledAuditLogColumns: IAuditLogDisabledColumnsMap = {timecard: {columns: ['group', 'occupation', 'template', 'overtimeReason', 'rate', 'factor', 'amount', 'accountCode', 'tags'], disableUdfs: true}};

export interface IAuditLogDisabledColumnsMap extends Partial<Record<AuditLogEntryType, IAuditLogDisabledColumns>> {}

export interface IAuditLogDisabledColumns {
  columns: string[];
  disableUdfs: boolean;
}

const nameSchema = 'id name';
const codeSchema = 'id code';

function formatTimeOffRangeTime(dt: any): string {
  return formatTime(dt) ?? '«All Day»';
}

export const booleanMapper         = (b?: boolean) => isNullOrUndefined(b) ? undefined : (b ? 'Yes' : 'No');

const dateHydrateFn                = (async (date: string)      => utc(date));
const timestampRangeStartHydrateFn = (async (effective: string) => new TimestampRange(effective).start);
const timestampRangeEndHydrateFn   = (async (effective: string) => new TimestampRange(effective).end);
const dateRangeStartHydrateFn      = (async (effective: string) => new DateRange(effective).start);
const dateRangeEndHydrateFn        = (async (effective: string) => new DateRange(effective).end);
const groupHydrateFn               = (async (id: string)        => (await DataQueries.getGroupsByIds(nameSchema, [id]))?.[0]);
const occupationHydrateFn          = (async (id: string)        => (await DataQueries.getOccupationsByIds(nameSchema, [id]))?.[0]);
const templateHydrateFn            = (async (id: string)        => (await DataQueries.getTemplatesByIds(codeSchema, [id]))?.[0]);
const employeeHydrateFn            = (async (id: string)        => (await DataQueries.getEmployeesByIds(nameSchema, [id]))?.[0]);
const reasonHydrateFn              = (async (id: string)        => (await DataQueries.getReasonsByIds(nameSchema, [id]))?.[0]);
const batchHydrateFn               = (async (id: string)        => (await DataQueries.getBatchesByIds(nameSchema, [id]))?.[0]);
const roleHydrateFn                = (async (id: string)        => (await DataQueries.getRolesByIds(nameSchema, [id]))?.[0]);

const _auditLogColumnDefns = {
  [AuditLogEntryTypes.Timecard]: [
      {name: 'earnedDate',      title: 'Date',            type: 'none',       width: '85px',  map: getDifferenceMapper(dateMapper),                                                       hydrateFn: dateHydrateFn},
      {name: 'group',           title: 'Group',           type: 'none',       width: '125px', map: getDifferenceMapper(nameMapper),        valueGetter: createGetter('groupId'),          hydrateFn: groupHydrateFn},
      {name: 'occupation',      title: 'Occupation',      type: 'none',       width: '125px', map: getDifferenceMapper(nameMapper),        valueGetter: createGetter('occupationId'),     hydrateFn: occupationHydrateFn},
      {name: 'template',        title: 'Shift Type',      type: 'none',       width: '125px', map: getDifferenceMapper(codeMapper),        valueGetter: createGetter('templateId'),       hydrateFn: templateHydrateFn},
      {name: 'effective.start', title: 'Start Time',      type: 'none',       width: '85px',  map: getDifferenceMapper(formatTime),        valueGetter: createGetter('effective'),        hydrateFn: timestampRangeStartHydrateFn},
      {name: 'effective.end',   title: 'End Time',        type: 'none',       width: '85px',  map: getDifferenceMapper(formatTime),        valueGetter: createGetter('effective'),        hydrateFn: timestampRangeEndHydrateFn},
      {name: 'employee',        title: 'Employee',        type: 'none',       width: '125px', map: getDifferenceMapper(nameMapper),        valueGetter: createGetter('employeeId'),       hydrateFn: employeeHydrateFn},
      {name: 'units',           title: 'Units',           type: numberColumn, width: '80px',  map: getDifferenceMapper(numberStringMapper) },
      {name: 'reason',          title: 'Reason',          type: 'none',       width: '125px', map: getDifferenceMapper(nameMapper),        valueGetter: createGetter('reasonId'),         hydrateFn: reasonHydrateFn},
      {name: 'overtimeReason',  title: 'Overtime Reason', type: 'none',       width: '125px', map: getDifferenceMapper(nameMapper),        valueGetter: createGetter('overtimeReasonId'), hydrateFn: reasonHydrateFn},
      {name: 'rate',            title: 'Rate',            type: 'none',       width: '80px', align: 'right'                                },
      {name: 'factor',          title: 'Factor',          type: 'none',       width: '80px', align: 'right'                                },
      {name: 'amount',          title: 'Amount',          type: numberColumn, width: '80px',  map: getDifferenceMapper(numberStringMapper) },
      {name: 'accountCode',     title: 'Account Code',    type: 'none',       width: '100px'                                               },
      {name: 'tags',            title: 'Tags',            type: 'none',       width: '125px', map: getDifferenceMapper(arrayMapper)        },
    ],
  [AuditLogEntryTypes.EmployeeTimecard]: [
      {name: 'earnedDate',      title: 'Date',             type: 'none', width: '85px',  map: getDifferenceMapper(dateMapper),                                            hydrateFn: dateHydrateFn},
      {name: 'employee',        title: 'Employee',         type: 'none', width: '125px', map: getDifferenceMapper(nameMapper),   valueGetter: createGetter('employeeId'), hydrateFn: employeeHydrateFn},
      {name: 'batch',           title: 'Batch',            type: 'none', width: '125px', map: getDifferenceMapper(nameMapper),   valueGetter: createGetter('batchId'),    hydrateFn: batchHydrateFn},
      {name: 'status',          title: 'Status',           type: 'none', width: '85px'                                           },
      {name: 'source',          title: 'Source',           type: 'none', width: '85px'                                           },
      {name: 'locked',          title: 'Locked',           type: 'none', width: '80px',  map: getDifferenceMapper(booleanMapper) },
      {name: 'currentVersion',  title: 'Current Version',  type: 'none', width: '140px', map: getDifferenceMapper(dateTimeMapper),                                        hydrateFn: dateHydrateFn},
      {name: 'exportedVersion', title: 'Exported Version', type: 'none', width: '140px', map: getDifferenceMapper(dateTimeMapper),                                        hydrateFn: dateHydrateFn},
    ],
  [AuditLogEntryTypes.Employee]: [
      {name: 'name',          title: 'Name',             type: 'none', width: '125px'                                            },
      {name: 'code',          title: 'Code',             type: 'none', width: '85px'                                             },
      {name: 'tags',          title: 'Tags',             type: 'none', width: '125px', map: getDifferenceMapper(arrayMapper)     },
      {name: 'email',         title: 'Email',            type: 'none', width: '125px'                                            },
      {name: 'phone.home',    title: 'Phone - Home',     type: 'none', width: '125px', map: getDifferenceMapper(getPhoneString), valueGetter: createGetter(['phone', 'home'])},
      {name: 'phone.mobile',  title: 'Phone - Mobile',   type: 'none', width: '125px', map: getDifferenceMapper(getPhoneString), valueGetter: createGetter(['phone', 'work'])},
      {name: 'phone.work',    title: 'Phone - Work',     type: 'none', width: '125px', map: getDifferenceMapper(getPhoneString), valueGetter: createGetter(['phone', 'mobile'])},
      {name: 'phone.fax',     title: 'Phone - Fax',      type: 'none', width: '125px', map: getDifferenceMapper(getPhoneString), valueGetter: createGetter(['phone', 'fax'])},
      {name: 'phone.workFax', title: 'Phone - Work Fax', type: 'none', width: '125px', map: getDifferenceMapper(getPhoneString), valueGetter: createGetter(['phone', 'workFax'])},
      {name: 'isPayable',     title: 'Is Paid',          type: 'none', width: '70px',  map: getDifferenceMapper(booleanMapper),  },
    ],
  [AuditLogEntryTypes.TimeOff]: [
      {name: 'effective.start', title: 'Effective Start', type: 'none',       width: '100px', map: getDifferenceMapper(dateMapper),         valueGetter: createGetter('effective'),  hydrateFn: dateRangeStartHydrateFn},
      {name: 'effective.end',   title: 'Effective End',   type: 'none',       width: '95px',  map: getDifferenceMapper(dateMapper),         valueGetter: createGetter('effective'),  hydrateFn: dateRangeEndHydrateFn},
      {name: 'employee',        title: 'Employee',        type: 'none',       width: '125px', map: getDifferenceMapper(nameMapper),         valueGetter: createGetter('employeeId'), hydrateFn: employeeHydrateFn},
      {name: 'status',          title: 'Status',          type: 'none',       width: '80px'                                            },
      {name: 'partialDay',      title: 'Partial Day',     type: 'none',       width: '75px',  map: getDifferenceMapper(booleanMapper), },
      {name: 'preference',      title: 'Preference',      type: 'number',     width: '75px'                                            },
      {name: 'comment',         title: 'Comment',         type: 'none',       width: '120px'                                           },
    ],
  [AuditLogEntryTypes.User]: [
      {name: 'name',          title: 'Name',             type: 'none', width: '125px'                                            },
      {name: 'code',          title: 'Code',             type: 'none', width: '85px'                                             },
      {name: 'tags',          title: 'Tags',             type: 'none', width: '125px', map: getDifferenceMapper(arrayMapper)     },
      {name: 'email',         title: 'Email',            type: 'none', width: '125px'                                            },
      {name: 'role',          title: 'Role',             type: 'none', width: '125px', map: getDifferenceMapper(nameMapper),     valueGetter: createGetter('roleId'),     hydrateFn: roleHydrateFn },
      {name: 'phone.home',    title: 'Phone - Home',     type: 'none', width: '125px', map: getDifferenceMapper(getPhoneString), valueGetter: createGetter(['phone', 'home'])},
      {name: 'phone.mobile',  title: 'Phone - Mobile',   type: 'none', width: '125px', map: getDifferenceMapper(getPhoneString), valueGetter: createGetter(['phone', 'work'])},
      {name: 'phone.work',    title: 'Phone - Work',     type: 'none', width: '125px', map: getDifferenceMapper(getPhoneString), valueGetter: createGetter(['phone', 'mobile'])},
      {name: 'phone.fax',     title: 'Phone - Fax',      type: 'none', width: '125px', map: getDifferenceMapper(getPhoneString), valueGetter: createGetter(['phone', 'fax'])},
      {name: 'phone.workFax', title: 'Phone - Work Fax', type: 'none', width: '125px', map: getDifferenceMapper(getPhoneString), valueGetter: createGetter(['phone', 'workFax'])},
      {name: 'employee',      title: 'Employee',         type: 'none', width: '125px', map: getDifferenceMapper(nameMapper),     valueGetter: createGetter('employeeId'), hydrateFn: employeeHydrateFn },
    ],
  [AuditLogEntryTypes.Role]: [
      {name: 'name',          title: 'Name',             type: 'none', width: '125px'                                            },
      {name: 'code',          title: 'Code',             type: 'none', width: '85px'                                             },
      {name: 'tags',          title: 'Tags',             type: 'none', width: '125px', map: getDifferenceMapper(arrayMapper)     },
      {name: 'workflow',      title: 'Workflow',         type: 'none', width: '85px'                                             },
    ],
};

const _timeOffRangesColumnDefns = [
  {name: 'effective.start', title: 'Start Time', type: 'none',       width: '85px',  map: getDifferenceMapper(formatTimeOffRangeTime), valueGetter: createGetter('effective'), hydrateFn: timestampRangeStartHydrateFn},
  {name: 'effective.end',   title: 'End Time',   type: 'none',       width: '85px',  map: getDifferenceMapper(formatTimeOffRangeTime), valueGetter: createGetter('effective'), hydrateFn: timestampRangeEndHydrateFn},
  {name: 'units',           title: 'Units',      type: numberColumn, width: '75px',  map: getDifferenceMapper(numberStringMapper)},
  {name: 'reason',          title: 'Reason',     type: 'none',       width: '125px', map: getDifferenceMapper(nameMapper),             valueGetter: createGetter('reasonId'),  hydrateFn: reasonHydrateFn},
];

async function getRoleSecurityTokensColumnDefns() {
  const securityTokensMap = new Map<string, string>();
  const securityTokens    = await authService.getSecurityTokens();
  for (const st of securityTokens.filter(st => st.path !== '«hidden»')) {
    securityTokensMap.set(st.token, `${st.path ? `${st.path} - ` : ''}${st.description}`);
  }
  const securityTokensMapper = (tokens?: string[]) => tokens?.map(t => securityTokensMap.get(t)).filter(t => !!t).sort();
  return ([
    {name: 'securityTokens', title: 'Security Rights', type: 'none', map: getDifferenceMapper(securityTokensMapper), renderer: AuditLogDifferenceCellStringArrayRenderer}
  ]);
}

async function getSecondaryGridColumnDefns(type: AuditLogEntryType) {
  if (type === 'timeoff') {
    return _timeOffRangesColumnDefns;
  }
  if (type === 'role') {
    return await getRoleSecurityTokensColumnDefns();
  }
}

const auditLogItemViewReturnSchema = `
  id
  description
  type
  operation
  createdBy
  createdDate
  effective
  before
  after
`;

interface IAuditLogItemViewState {
  isLoading:         boolean;
  selectedAuditLog?: IAuditLog;
  disabledColumns:   IAuditLogDisabledColumnsMap;
}

export class AuditLogItemViewModel {
  state: IAuditLogItemViewState;
  watchSelectedAuditLog: WatcherTypeFn;
  grid?: IGrid;
  secondaryGrid?: IGrid;
  secondaryGridTitle?: string;

  constructor() {
    this.state = observable({
      isLoading: false,
      selectedAuditLog: undefined,
      disabledColumns: {},
    });

    this.watchSelectedAuditLog = createWatcherFn();
    this.grid               = undefined;
    this.secondaryGrid      = undefined;
    this.secondaryGridTitle = undefined;
  }

  reset() {
    this.watchSelectedAuditLog();
    this.selectedAuditLog = undefined;
    this.recreateGrid();
  }

  async load() {
    this.watchSelectedAuditLog(() => auditLogListModel.selectedAuditLog, async () => {
      await this.selectAuditLog();
    });

    await this.selectAuditLog();
  }

  get isLoading() {
    return this.state.isLoading;
  }

  get disabledColumns(): IAuditLogDisabledColumnsMap {
    return this.state.disabledColumns;
  }

  set disabledColumns(disabledColumns: IAuditLogDisabledColumnsMap) {
    this.state.disabledColumns = disabledColumns;
  }

  get selectedAuditLog(): IAuditLog | undefined {
    return this.state.selectedAuditLog as IAuditLog | undefined;
  }

  set selectedAuditLog(auditLog: IAuditLog | undefined) {
    this.state.selectedAuditLog = auditLog;
  }

  async selectAuditLog() {
    this.state.isLoading = true;
    this.selectedAuditLog = await this.getAuditLog(auditLogListModel.selectedAuditLog?.id);
    await this.recreateGrid();
    this.state.isLoading = false;
  }

  async getAuditLog(auditLogId?: string) {
    if (isNullOrUndefinedOrEmpty(auditLogId)) {
      return undefined;
    }
    const results = await DataQueries.getAuditLogs(auditLogItemViewReturnSchema, {ids: [auditLogId]});
    return results?.data?.[0] as IAuditLog | undefined;
  }

  protected getColumnDefnsByAuditLogType(type: AuditLogEntryType): any[] {
    const columnDefns       = [..._auditLogColumnDefns[type]] as any[];
    const disableUdfColumns = this.disabledColumns[type]?.disableUdfs;
    if (!disableUdfColumns) {
      columnDefns.push(...this.getUdfColumnDefns(type));
    }
    if (this.disabledColumns[type]) {
      for (let i = columnDefns.length - 1; i >= 0; i--) {
        if (this.disabledColumns[type]!.columns?.includes(columnDefns[i].name)) {
          columnDefns.splice(i, 1);
        }
      }
    }

    return columnDefns;
  }

  protected getUdfColumnDefns(type: AuditLogEntryType) {
    const columnDefns: any[] = [];
    const udfType            = _auditLogEntryTypeToUdfTypeMap[type];
    const udfs               = !isNullOrUndefined(udfType) ? udfsSubscriptionService.subscriptionModel.getUdfsByDocumentType(udfType) : [];

    for (const udf of udfs) {
      const columnAlign = udf.type === 'float' || udf.type === 'integer' ? 'right' : 'left';
      const innerMapFn  = ((v: any) => (formatUdfValue(udf, v) || ''));
      columnDefns.push({name: `fields.${udf.name}`, title: udf.displayName, type: 'none', width: '125px', align: columnAlign, map: getDifferenceMapper(innerMapFn), cellTooltip: getDifferenceCellTooltip, renderer: AuditLogDifferenceCellRenderer, valueGetter: createGetter(['fields', udf.name])});
    }

    return columnDefns;
  }

  async recreateGrid() {
    if (this.selectedAuditLog) {
      const colDefns         = this.getColumnDefnsByAuditLogType(this.selectedAuditLog.type);
      const cols: IColumn[]  = this.createGridColumns(colDefns);
      const rows: IRowData[] = await this.createGridRows(colDefns);
      this.grid              = createGrid(rows, cols, this.createGridOptions());
      await this.recreateSecondaryGrid();
    } else {
      this.grid               = undefined;
      this.secondaryGrid      = undefined;
      this.secondaryGridTitle = undefined;
    }
  }

  protected async recreateSecondaryGrid() {
    const type = this.selectedAuditLog?.type;
    if (type !== 'timeoff' && type !== 'role') {
      this.secondaryGrid      = undefined;
      this.secondaryGridTitle = undefined;
      return;
    }

    const columnDefns     = await getSecondaryGridColumnDefns(type) as any[];
    const cols: IColumn[] = this.createGridColumns(columnDefns);
    let rows: IRowData[]  = [];
    if (type === 'timeoff') {
      rows = await this.createGridRowsFromArrayField(columnDefns, 'timeOffRanges');
      this.secondaryGridTitle = 'Time Off Ranges';
    } else if (type === 'role') {
      rows = await this.createGridRows(columnDefns);
      this.secondaryGridTitle = 'Permissions';
    }

    this.secondaryGrid = createGrid(rows, cols, this.createGridOptions());
  }

  protected createGridColumns(colDefns: any[]) {
    const cols: IColumn[] = [];
    for (const colDefn of colDefns) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { name, title, valueGetter, renderer, ...opts } = colDefn;
      opts.canSort     = false;
      opts.cellTooltip = getDifferenceCellTooltip;
      opts.renderer    = renderer ?? AuditLogDifferenceCellRenderer;
      const column = createColumn(
        name,
        title,
        opts
      );
      cols.push(column);
    }

    return cols;
  }

  protected async createGridRows(colDefns: any[]) {
    const rows: IRowData[] = [];
    if (this.selectedAuditLog) {
      const r: IRowData = { id: `${guid()}`, data: {} };
      const beforeRow   = this.selectedAuditLog.before;
      const afterRow    = this.selectedAuditLog.after;
      const hydrateArr  = [] as any[];
      for (const colDefn of colDefns) {
        const fieldName    = colDefn.name;
        const v            = {value1: undefined, value2: undefined};
        v.value1           = colDefn.valueGetter ? colDefn.valueGetter(beforeRow) : beforeRow?.[fieldName];
        v.value2           = colDefn.valueGetter ? colDefn.valueGetter(afterRow) : afterRow?.[fieldName];
        r.data![fieldName] = v;
        if (colDefn.hydrateFn) {
          hydrateArr.push({hydrateFn: colDefn.hydrateFn, value: v});
        }
      }

      if (hydrateArr.length) {
        await this.hydrateData(hydrateArr);
      }

      rows.push(r);
    }

    return rows;
  }

  protected async createGridRowsFromArrayField(colDefns: any[], arrayField: string) {
    const rows: IRowData[] = [];
    if (this.selectedAuditLog) {
      const beforeRows    = this.selectedAuditLog.before?.[arrayField] as any[];
      const afterRows     = this.selectedAuditLog.after?.[arrayField] as any[];
      const maxRowsLength = Math.max(beforeRows?.length ?? 0, afterRows?.length ?? 0);
      const hydrateArr    = [] as any[];

      for (let i = 0; i < maxRowsLength; i++) {
        const r: IRowData = { id: `${guid()}`, data: {} };
        const beforeRow   = beforeRows?.[i];
        const afterRow    = afterRows?.[i];

        for (const colDefn of colDefns) {
          const fieldName    = colDefn.name;
          const v            = {value1: undefined, value2: undefined};
          v.value1           = colDefn.valueGetter ? colDefn.valueGetter(beforeRow) : beforeRow?.[fieldName];
          v.value2           = colDefn.valueGetter ? colDefn.valueGetter(afterRow) : afterRow?.[fieldName];
          r.data![fieldName] = v;
          if (colDefn.hydrateFn) {
            hydrateArr.push({hydrateFn: colDefn.hydrateFn, value: v});
          }
        }

        rows.push(r);
      }

      if (hydrateArr.length) {
        await this.hydrateData(hydrateArr);
      }
    }

    return rows;
  }

  protected async hydrateData(hydrateArr: {hydrateFn: (value: any) => any, value: {value1: any, value2: any}}[]) {
    const hydratePromises = [] as Promise<void>[];
    for (const col of hydrateArr) {
      if (!isNullOrUndefinedOrEmpty(col.value.value1)) hydratePromises.push((async () => col.value.value1 = await col.hydrateFn(col.value.value1))());
      if (!isNullOrUndefinedOrEmpty(col.value.value2)) hydratePromises.push((async () => col.value.value2 = await col.hydrateFn(col.value.value2))());
    }
    await Promise.all(hydratePromises);
  }

  protected createGridOptions(): IGridOptions {
    return {
      readOnly:             true,
      clearSelectionOnBlur: true,
      variableKeybinds: {
        'Ctrl+Insert': (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
        'Ctrl+D':      (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
        'Ctrl+Delete': (_evt: React.KeyboardEvent<any>, _cell: ICell) => {}
      }
    };
  }

  get selectedAuditLogCreatedDate() {
    return this.selectedAuditLog?.createdDate?.toTimestampString() ?? 'N/A';
  }

  get selectedAuditLogCreatedBy() {
    const createdBy = this.selectedAuditLog?.createdBy;
    return !isNullOrUndefinedOrEmpty(createdBy) && createdBy !== '«unknown»' ? createdBy : 'System';
  }

  get selectedAuditLogType() {
    return this.selectedAuditLog?.type ? _auditLogEntryTypeMap[this.selectedAuditLog.type] : 'N/A';
  }

  get selectedAuditLogOperation() {
    return this.selectedAuditLog?.operation ?? 'N/A';
  }

  get selectedAuditLogEffective() {
    return this.selectedAuditLog?.effective.toDateString() ?? 'N/A';
  }

  get selectedAuditLogDescription() {
    return this.selectedAuditLog?.description ?? 'N/A';
  }
}

const auditLogItemViewModel = new AuditLogItemViewModel();
export default auditLogItemViewModel;
